import { z } from 'zod';
/**
 * Cap (trycap.dev) captcha token forwarded by the dashboard / admin
 * signin/signup forms. Optional at the schema level so that:
 *   1. clients with captcha disabled (or older clients) keep working;
 *   2. `validateBody` doesn't strip or reject the field before the
 *      `requireCaptcha` middleware can read it from `req.body`.
 *
 * Whether the token is REQUIRED at runtime is decided by `requireCaptcha`
 * based on the per-instance settings (DB) and the `CAP_DISABLE` env knob.
 */
const CaptchaToken = z.string().min(1).max(4096).optional();
/**
 * Six-digit TOTP code as printed by an authenticator app. Stored as a
 * string (not a number) so leading zeros and constant-time comparison
 * are preserved.
 */
const TotpCode = z.string().regex(/^\d{6}$/);
/**
 * Recovery code shape printed at enrollment. Format is
 * `XXXX-XXXX-XXXX` (12 base32-style chars + 2 dashes) but we accept any
 * length in the [8, 64] range to stay forward-compatible with future
 * regeneration formats.
 */
const RecoveryCode = z.string().min(8).max(64);
export const SignupInput = z.object({
    email: z.string().email().toLowerCase(),
    password: z.string().min(8).max(128),
    name: z.string().min(1).max(100).optional(),
    captchaToken: CaptchaToken,
});
export const LoginInput = z.object({
    email: z.string().email().toLowerCase(),
    password: z.string().min(1),
    captchaToken: CaptchaToken,
});
export const UserRole = z.enum(['owner', 'admin', 'agent']);
export const OnlineStatus = z.enum(['online', 'away', 'offline']);
export const UserDTO = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string().nullable(),
    role: UserRole,
    /**
     * For `agent` users this is the id of their owning account; the team
     * scope (websites, conversations, billing) is keyed off this value. For
     * `owner` and `admin` users this is the user's own id, so callers can
     * uniformly treat `effectiveOwnerId` as the team boundary.
     */
    effectiveOwnerId: z.string().uuid(),
    ownerId: z.string().uuid().nullable(),
    onlineStatus: OnlineStatus,
    /**
     * Mirrors `users.two_factor_enabled` so the dashboard/admin can render
     * the "set up 2FA" nudge banner without a second round-trip.
     */
    twoFactorEnabled: z.boolean(),
    /**
     * Mirrors `users.email_verified_at != null`. A session is only ever
     * issued to verified accounts, so within an authenticated dashboard
     * this is effectively always `true`; it is included for completeness
     * and for admin tooling that inspects accounts.
     */
    emailVerified: z.boolean(),
    createdAt: z.string(),
});
/**
 * Successful login / refresh response. When 2FA is enabled on the
 * account, /v1/auth/login returns `TwoFactorChallengeResponse` instead;
 * see `LoginResponse` below.
 */
export const AuthResponse = z.object({
    user: UserDTO,
    accessToken: z.string(),
});
/**
 * Short-lived ticket returned when /v1/auth/login succeeds at the
 * password step but the user has 2FA enabled. The client must POST it
 * back to /v1/auth/2fa/login together with a TOTP or recovery code to
 * complete the sign-in.
 */
export const TwoFactorChallengeResponse = z.object({
    requires2fa: z.literal(true),
    twoFactorToken: z.string().min(1),
    expiresAt: z.string(),
});
/**
 * Discriminated union of what /v1/auth/login can return: the regular
 * success shape, or a 2FA challenge.
 */
export const LoginResponse = z.union([AuthResponse, TwoFactorChallengeResponse]);
export function isTwoFactorChallenge(r) {
    return 'requires2fa' in r && r.requires2fa === true;
}
// ----- email verification -----
/**
 * Response of POST /v1/auth/signup. Signup no longer establishes a
 * session: the account is created unverified and a confirmation email is
 * sent. The client shows a "check your inbox" screen and the user becomes
 * able to sign in only after clicking the emailed link.
 */
export const SignupResponse = z.object({
    requiresVerification: z.literal(true),
    email: z.string().email(),
});
/**
 * Body of POST /v1/auth/verify-email. The raw token comes from the link
 * in the confirmation email; the server matches sha256(token). On success
 * the account is marked verified AND a full session is issued (auto
 * sign-in), so the response is a regular `AuthResponse`.
 */
export const VerifyEmailInput = z.object({
    token: z.string().min(20).max(128),
});
/**
 * Body of POST /v1/auth/resend-verification. Constant-response (204)
 * regardless of whether the email matches an unverified account, to avoid
 * account enumeration. `captchaToken` is gated by `requireCaptcha`.
 */
export const ResendVerificationInput = z.object({
    email: z.string().email().toLowerCase(),
    captchaToken: CaptchaToken,
});
// ----- account self-service password change -----
export const ChangePasswordInput = z.object({
    currentPassword: z.string().min(1).max(128),
    newPassword: z.string().min(8).max(128),
    /** REQUIRED when the caller has 2FA enabled; ignored otherwise. */
    twoFactorCode: TotpCode.optional(),
});
// ----- 2FA enrollment + status -----
export const TwoFactorStatusDTO = z.object({
    enabled: z.boolean(),
    enrolledAt: z.string().nullable(),
    /** Number of UNUSED recovery codes still on file. */
    recoveryCodesRemaining: z.number().int().nonnegative(),
});
/**
 * Response of POST /v1/account/2fa/setup. `qrDataUrl` is a PNG data URL
 * the client can render directly; `secret` is the base32 form the user
 * can type into their authenticator app if scanning fails.
 */
export const TwoFactorSetupResponse = z.object({
    otpauthUri: z.string().min(1),
    qrDataUrl: z.string().min(1),
    secret: z.string().min(1),
});
export const TwoFactorVerifyInput = z.object({
    code: TotpCode,
});
/**
 * Plaintext recovery codes shown ONCE after a successful enrollment or
 * regenerate; the server stores only Argon2 hashes.
 */
export const TwoFactorVerifyResponse = z.object({
    recoveryCodes: z.array(z.string().min(1)).length(10),
});
/**
 * Disable 2FA. Caller must prove possession of either an authenticator
 * code OR a recovery code, plus their password. We accept either of the
 * codes via a discriminator in the body so the UI flow stays simple.
 */
export const TwoFactorDisableInput = z
    .object({
    currentPassword: z.string().min(1).max(128),
    code: TotpCode.optional(),
    recoveryCode: RecoveryCode.optional(),
})
    .refine((v) => Boolean(v.code) !== Boolean(v.recoveryCode), {
    message: 'Provide exactly one of `code` or `recoveryCode`',
});
export const RegenerateRecoveryCodesInput = z.object({
    currentPassword: z.string().min(1).max(128),
    code: TotpCode,
});
/**
 * Body of POST /v1/auth/2fa/login. Exactly one of `code` or
 * `recoveryCode` must be present; the refine handles the xor.
 *
 * `captchaToken` is forwarded to the same `requireCaptcha` middleware
 * that gates /login and /signup. The dashboard / admin acquire a
 * FRESH token between the password step and this challenge step
 * (Cap tokens are single-use), and the field is left optional at the
 * schema level so the middleware can be the source of truth for
 * "captcha is currently required" — matching the rest of /v1/auth.
 */
export const TwoFactorChallengeInput = z
    .object({
    twoFactorToken: z.string().min(1),
    code: TotpCode.optional(),
    recoveryCode: RecoveryCode.optional(),
    captchaToken: CaptchaToken,
})
    .refine((v) => Boolean(v.code) !== Boolean(v.recoveryCode), {
    message: 'Provide exactly one of `code` or `recoveryCode`',
});
// ----- password reset -----
export const ForgotPasswordInput = z.object({
    email: z.string().email().toLowerCase(),
    captchaToken: CaptchaToken,
});
export const ResetPasswordInput = z.object({
    token: z.string().min(20).max(128),
    newPassword: z.string().min(8).max(128),
    twoFactorCode: TotpCode.optional(),
    recoveryCode: RecoveryCode.optional(),
});
//# sourceMappingURL=auth.js.map